import { trackEvent } from 'lib/posthog';

let hasStarted = false;

const round = (n) =>
  typeof n === 'number' ? Math.round(n * 10000) / 10000 : null;

// Four is what the store-page shifts actually carry, and the cap keeps one
// pathological entry from dominating the payload
const MAX_REPORTED_SOURCES = 4;
const MAX_SOURCES_LENGTH = 200;

// Appended when labels were dropped, so a short list is never mistaken for a
// complete one. Reserved out of the budget rather than added on top, which is
// what keeps the field within MAX_SOURCES_LENGTH in every case
const SOURCES_TRUNCATED_MARKER = ';+';

const REGION_SELECTOR = '[data-cls-region]';

/**
 * Names the marked region the shifted node belongs to.
 *
 * `largestShiftTarget` cannot do this. The selector generator of web-vitals
 * walks up the tree and stops as soon as the next segment would exceed its
 * 100-character cap, and a wrapper whose parent carries a long class list gets
 * truncated down to its own segment. Two elements sharing such a parent then
 * report the same `target`, and the field data cannot say which one moved.
 *
 * Reading a marker instead of lengthening the selector keeps `target` byte-for
 * byte identical, so samples collected before this field stay comparable. An
 * `id` on each wrapper would also disambiguate, since the generator returns
 * `#id` and stops, but it would rewrite `target` and break that continuity.
 *
 * `LayoutShiftAttribution.node` is a `Node`, not an `Element`, so a text node
 * is a valid source and has no `closest`. Falling back to `parentElement` keeps
 * its region instead of discarding it — an accordion expanding in `store-faq`
 * reports text nodes, and dropping them would lose the one region that explains
 * the shift.
 */
const regionOf = (node) => {
  const scope = node.closest ? node : node.parentElement;
  return scope?.closest?.(REGION_SELECTOR)?.dataset?.clsRegion ?? null;
};

/**
 * Counts marked regions present in the document.
 *
 * `shift_region` being null is ambiguous on its own: it means either that the
 * node sits outside every marked wrapper, which is normal, or that no marker
 * reached the DOM at all, which is the failure this instrumentation is most
 * likely to hit. A zero here separates the two, and without it a week of empty
 * collection reads as "shifts happen outside the marked regions".
 *
 * Sits outside the atomic block on purpose: it describes the page, not the
 * shift, so it stays meaningful precisely in the case where the block is null.
 */
const countMarkedRegions = () =>
  document.querySelectorAll(REGION_SELECTOR).length;

/**
 * Labels one source node for reading, not for joining.
 *
 * A marked region wins when there is one. Otherwise this falls back to the tag
 * plus the first two classes in author order, which is deliberately NOT the
 * format `target` uses: that one sorts classes and walks ancestors. Do not
 * compare the two in a query, and do not try to make them match — the point of
 * this field is to read what moved alongside the attributed element, which no
 * existing field reports.
 */
const identifySource = (node) => {
  const region = regionOf(node);
  if (region) return region;
  if (node.nodeType !== Node.ELEMENT_NODE) return node.nodeName ?? 'node';

  const tag = node.tagName.toLowerCase();
  const classes =
    typeof node.className === 'string'
      ? node.className.trim().split(/\s+/).filter(Boolean).slice(0, 2)
      : [];

  return classes.length ? `${tag}.${classes.join('.')}` : tag;
};

/**
 * Serializes the co-movers of the largest shift as `id@deltaTop/deltaHeight`,
 * joined by `;`.
 *
 * The attributed element answers "what moved", never "why". An element that was
 * pushed rather than grown has its cause among the sibling sources of the same
 * entry, so identifying them is what turns a displacement into a lead.
 *
 * Sources without a node or without both rects are skipped rather than emitted
 * as placeholders: a label with no deltas cannot be acted on, and padding the
 * list would misreport how many elements actually moved. Filtering happens
 * before the count cap, so four unusable sources at the head of the array do
 * not consume the budget and leave the real co-movers unreported.
 *
 * Both caps drop whole labels. Cutting the joined string at a character offset
 * would truncate a delta instead of a name — `div.grid@128/0` becomes
 * `div.grid@12`, which still parses as `id@top/height` and enters the analysis
 * as a 128px shift reported as 12px. A short list is recoverable; a plausible
 * wrong number is not.
 *
 * Deltas are integers here while `shift_delta_top` keeps four decimals for the
 * same node. That is intentional: this field is read by eye, and pixel
 * precision is enough to tell a push from a growth.
 */
const describeSources = (entry) => {
  const describable = (entry?.sources ?? []).filter(
    ({ node, previousRect, currentRect }) => node && previousRect && currentRect
  );
  if (!describable.length) return null;

  const budget = MAX_SOURCES_LENGTH - SOURCES_TRUNCATED_MARKER.length;
  const labels = [];
  let used = 0;
  // Stop at the first label that does not fit rather than trying the next one:
  // a later, shorter label would slip in out of order and read as if the
  // skipped source had never moved
  let full = false;
  let dropped = describable.length > MAX_REPORTED_SOURCES;

  describable.slice(0, MAX_REPORTED_SOURCES).forEach((source) => {
    if (full) return;

    const { node, previousRect, currentRect } = source;
    const deltaTop = Math.round(currentRect.top - previousRect.top);
    const deltaHeight = Math.round(currentRect.height - previousRect.height);
    const label = `${identifySource(node)}@${deltaTop}/${deltaHeight}`;
    const cost = labels.length ? label.length + 1 : label.length;

    if (used + cost > budget) {
      full = true;
      dropped = true;
      return;
    }

    labels.push(label);
    used += cost;
  });

  if (!labels.length) return null;

  return dropped
    ? `${labels.join(';')}${SOURCES_TRUNCATED_MARKER}`
    : labels.join(';');
};

/**
 * Splits "the attributed element grew" from "it was pushed by something above".
 *
 * `largestShiftTarget` names the element that moved, which is not necessarily
 * the one that caused the move. Comparing the source's rects settles it: a
 * changed `top` with an unchanged `height` means the element was displaced and
 * the cause sits above it; a grown `height` means the element is the cause.
 *
 * The deltas come from `largestShiftSource` rather than from walking
 * `largestShiftEntry.sources`. Its node is the same one web-vitals resolves
 * into `largestShiftTarget`, so the selector already in the event identifies
 * these deltas, in a format the other sources cannot match — the library's
 * selector generator is not a public export. `shift_sources` carries those
 * other sources under labels of its own, deliberately not comparable to
 * `target`.
 *
 * A `LayoutShift` can be attributed with no source, and a source's node is
 * null once the element leaves the DOM. Both yield nulls rather than a throw:
 * the rects survive a null node, but `largestShiftTarget` comes from that same
 * node, so the deltas would describe an element nothing can name.
 *
 * The five fields are one atomic block: they are emitted together or nulled
 * together, so a sample is either wholly attributable or wholly absent. A row
 * carrying a count but no deltas reads as "shift with sources we chose not to
 * measure", which is not a state that exists — the gate filters on them
 * together. Do not lift any of them out to compute it independently of the
 * source; that is exactly the split an earlier review rejected.
 *
 * `shift_region` has a second null case of its own, and it is not a failure:
 * the node is alive but no marked ancestor sits above it, which is every
 * element outside the marked candidates.
 *
 * The two DOM-reading fields carry their own `try`. They walk live nodes at
 * unload time, which is the least predictable moment in the page's life, and
 * they are evaluated while building the `trackEvent` argument — so without this
 * guard a throw in either would cancel the whole call and take `value`,
 * `rating`, `target` and the already-working deltas with it. Newer diagnostics
 * must not be able to regress the ones that already earn their keep.
 */
const domDerivedFields = (source, entry) => {
  try {
    return {
      shift_region: regionOf(source.node),
      shift_sources: describeSources(entry),
    };
  } catch {
    return { shift_region: null, shift_sources: null };
  }
};

const attributableShiftFields = (attribution) => {
  const source = attribution.largestShiftSource;
  const previous = source?.previousRect;
  const current = source?.currentRect;

  if (!source?.node || !previous || !current) {
    return {
      shift_delta_top: null,
      shift_delta_height: null,
      sources_count: null,
      shift_region: null,
      shift_sources: null,
    };
  }

  return {
    shift_delta_top: round(current.top - previous.top),
    shift_delta_height: round(current.height - previous.height),
    sources_count: attribution.largestShiftEntry?.sources?.length ?? null,
    ...domDerivedFields(source, attribution.largestShiftEntry),
  };
};

/**
 * Reports to Sentry without ever rejecting.
 *
 * Both callers run where a rejection would be invisible: a catch handler and
 * an unload-time callback. If Sentry itself cannot load there is nowhere left
 * to report, so the failure is logged and dropped rather than surfacing as an
 * unhandled rejection.
 */
const captureError = (error) =>
  import('@sentry/nextjs')
    .then(({ captureException }) => {
      captureException(error);
    })
    .catch(() => {
      // eslint-disable-next-line no-console
      console.warn('[webVitals] could not report CLS failure to Sentry', error);
    });

/**
 * Reports the page's final CLS score plus the element that shifted most.
 *
 * CrUX exposes only an aggregate p75 with no attribution, and the shift
 * does not reproduce in a lab session, so `largestShiftTarget` gathered
 * from real visits is the only way to identify which element moves.
 *
 * Uses the `try` nature: the taxonomy defines it for system events with
 * no direct user interaction, which is what a performance measurement is.
 * `source_type: 'system'` marks it as not user-initiated.
 *
 * `onCLS` reports once per page visit, when the page is backgrounded or
 * unloaded, and keeps accumulating across soft navigations rather than
 * resetting. The module-level guard matches that lifetime: re-arming on
 * every route change would register duplicate listeners for one visit.
 *
 * Returns the import promise so tests can await registration deterministically.
 * Callers in render code ignore it.
 *
 * @returns {Promise<void>}
 */
const reportCls = () => {
  if (typeof window === 'undefined' || hasStarted) return Promise.resolve();

  hasStarted = true;

  // `onCLS` observes layout-shift with `buffered: true`, so registering after
  // a soft navigation replays shifts from the route the visit started on
  //
  // There is no API to flush that buffer, so record where measurement was
  // armed and drop contaminated samples at analysis time. PostHog attaches
  // the report-time URL as `$current_url`, so only the armed path is needed
  const armedPath = window.location.pathname;

  return import('web-vitals/attribution')
    .then(({ onCLS }) => {
      onCLS(({ value, rating, navigationType, attribution }) => {
        // onCLS fires as the page is being backgrounded or unloaded, where a
        // throw would be swallowed by the browser. Diagnostic instrumentation
        // must never be the reason a visit ends badly
        try {
          trackEvent('try web vital cls', {
            source_type: 'system',
            status: 'success',
            value: round(value),
            rating,
            navigation_type: navigationType,
            // Names the element that moved, not the one that caused the
            // move. Pair it with shift_time to infer what appeared above
            target: attribution.largestShiftTarget ?? null,
            shift_value: round(attribution.largestShiftValue),
            // Separates markup rendering late (early ms) from async content
            // such as images or third parties (seconds in)
            shift_time:
              typeof attribution.largestShiftTime === 'number'
                ? Math.round(attribution.largestShiftTime)
                : null,
            load_state: attribution.loadState ?? null,
            armed_path: armedPath,
            // Separates "no marked region contains this node" from "no marker
            // ever reached the DOM" — a zero here invalidates every null
            // shift_region on the page instead of letting them read as data
            regions_marked: countMarkedRegions(),
            ...attributableShiftFields(attribution),
          });
        } catch (error) {
          captureError(error);
        }
      });
    })
    .catch(captureError);
};

export default reportCls;
